import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { api, rp, CHECKLIST_ITEMS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export default function Inspections() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(null);
  const [ai, setAi] = useState({ busy: false, text: "" });

  const load = () => {
    api.get("/inspections").then((r) => setRows(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/units").then((r) => setUnits(r.data));
  };
  useEffect(() => { load(); }, []);

  const start = () => { setAi({ busy: false, text: "" }); setForm({
    booking_id: "", rental_unit_ids: [], checklist: Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i, "good"])),
    issue_description: "", missing_item: "", customer_charge: 0, repair_cost: 0, deposit_deduction: 0, notes: "", photos: [],
  }); };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    const b64 = await Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setForm((p) => ({ ...p, photos: [...p.photos, ...b64] }));
  };

  const save = async () => {
    if (!form.booking_id) return toast.error("Select a booking");
    try {
      await api.post("/inspections", {
        ...form, customer_charge: Number(form.customer_charge || 0), repair_cost: Number(form.repair_cost || 0),
        deposit_deduction: Number(form.deposit_deduction || 0),
      });
      setForm(null); load(); toast.success("Inspection saved — booking marked Completed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiSummary = async () => {
    if (!form.booking_id) return toast.error("Pilih booking dulu");
    setAi({ busy: true, text: "" });
    try {
      const r = await api.post("/ai/inspection-summary", {
        booking_id: form.booking_id,
        draft: {
          checklist: form.checklist, missing_item: form.missing_item,
          repair_cost: Number(form.repair_cost || 0), issue_description: form.issue_description,
          inspection_date: new Date().toISOString().slice(0, 10),
        },
      });
      setAi({ busy: false, text: r.data.summary });
    } catch (e) {
      setAi({ busy: false, text: "" });
      toast.error(e?.response?.data?.detail || "AI gagal membuat ringkasan");
    }
  };

  const bkg = (id) => bookings.find((b) => b.id === id);

  return (
    <div data-testid="admin-inspections">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl tracking-tight">Return Inspection</h1>
        <button data-testid="new-inspection-btn" onClick={start} className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold">+ New Inspection</button>
      </div>

      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Date</th><th className="p-3">Booking</th><th className="p-3">Issues</th><th className="p-3">Charge</th>
              <th className="p-3">Repair</th><th className="p-3">Deposit Deducted</th><th className="p-3">Photos</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((i) => {
              const issues = Object.entries(i.checklist || {}).filter(([, v]) => v === "issue").map(([k]) => k);
              return (
                <tr key={i.id} data-testid={`inspection-row-${i.id}`}>
                  <td className="p-3">{i.inspection_date}</td>
                  <td className="p-3">{bkg(i.booking_id)?.booking_code || "—"}</td>
                  <td className="p-3 text-xs">{issues.length ? issues.join(", ") : <span className="text-emerald-700">All good</span>}</td>
                  <td className="p-3">{rp(i.customer_charge)}</td><td className="p-3">{rp(i.repair_cost)}</td>
                  <td className="p-3">{rp(i.deposit_deduction)}</td><td className="p-3">{(i.photos || []).length}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="p-5 text-ink-soft">No inspections yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>New Return Inspection</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4 text-sm">
              <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Booking</span>
                <select data-testid="insp-booking" className={sel} value={form.booking_id} onChange={(e) => setForm({ ...form, booking_id: e.target.value })}>
                  <option value="">Select booking…</option>
                  {bookings.map((b) => <option key={b.id} value={b.id}>{b.booking_code} — {b.customer_name} ({b.rental_date})</option>)}
                </select>
              </label>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-2">Units Inspected</div>
                <div className="flex flex-wrap gap-2">
                  {units.map((u) => {
                    const on = form.rental_unit_ids.includes(u.id);
                    return <button key={u.id} onClick={() => setForm({ ...form, rental_unit_ids: on ? form.rental_unit_ids.filter((x) => x !== u.id) : [...form.rental_unit_ids, u.id] })}
                      className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-ink text-cream border-ink" : "border-black/15"}`}>{u.unit_code}</button>;
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-2">Checklist</div>
                <div className="space-y-1.5">
                  {CHECKLIST_ITEMS.map((item) => (
                    <div key={item} className="flex items-center justify-between gap-3 border border-black/10 rounded-md px-3 py-2">
                      <span>{item}</span>
                      <div className="flex gap-1.5">
                        {["good", "issue"].map((v) => (
                          <button key={v} data-testid={`chk-${item}-${v}`} onClick={() => setForm({ ...form, checklist: { ...form.checklist, [item]: v } })}
                            className={`text-xs px-3 py-1 rounded-full border ${form.checklist[item] === v ? (v === "good" ? "bg-emerald-100 border-emerald-300" : "bg-red-100 border-red-300") : "border-black/15"}`}>
                            {v === "good" ? "Good" : "Issue"}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="sm:col-span-2 block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Issue Description</span>
                  <Textarea data-testid="insp-issue" rows={2} value={form.issue_description} onChange={(e) => setForm({ ...form, issue_description: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Missing Item</span>
                  <Input value={form.missing_item} onChange={(e) => setForm({ ...form, missing_item: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Customer Charge</span>
                  <Input type="number" value={form.customer_charge} onChange={(e) => setForm({ ...form, customer_charge: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Repair Cost</span>
                  <Input type="number" value={form.repair_cost} onChange={(e) => setForm({ ...form, repair_cost: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Deposit Deduction</span>
                  <Input data-testid="insp-deduction" type="number" value={form.deposit_deduction} onChange={(e) => setForm({ ...form, deposit_deduction: e.target.value })} /></label>
                <label className="sm:col-span-2 block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Photos</span>
                  <input data-testid="insp-photos" type="file" accept="image/*" multiple onChange={onFiles} className="mt-1 block text-sm" /></label>
                {form.photos.length > 0 && <div className="sm:col-span-2 flex gap-2 flex-wrap">
                  {form.photos.map((p, i) => <img key={`photo-${i}`} src={p} alt="" className="w-20 h-20 object-cover rounded-md border border-black/10" />)}</div>}
                <label className="sm:col-span-2 block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Internal Notes</span>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
              </div>
              <div className="rounded-md border border-lm-gold/40 bg-lm-gold/5 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-semibold flex items-center gap-1.5">
                    <Sparkles size={14} className="text-lm-gold" /> Ringkasan &amp; saran potong deposit (AI)
                  </div>
                  <button data-testid="insp-ai-btn" onClick={aiSummary} disabled={ai.busy}
                    className="h-9 px-3 rounded-md bg-ink text-cream text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                    {ai.busy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Buat Ringkasan
                  </button>
                </div>
                {ai.text && <pre data-testid="insp-ai-summary" className="mt-2 whitespace-pre-wrap font-sans text-xs text-ink-soft">{ai.text}</pre>}
              </div>
              <div className="flex gap-2">
                <button data-testid="insp-save" onClick={save} className="lm-btn-primary rounded-md px-5 py-2.5 font-semibold">Save Inspection</button>
                <button onClick={() => setForm(null)} className="rounded-md px-5 py-2.5 border border-black/15">Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
